import React from 'react';
import { Scale } from 'lucide-react';

const SERIF = "'Cormorant Garamond', serif";

// Subtle four-edge feather so the product shot melts into the taupe field
// instead of ending on a hard rectangle.
const FEATHER = 'linear-gradient(to right, transparent 0, #000 2%, #000 98%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 2%, #000 98%, transparent 100%)';
const FEATHER_STYLE = {
  WebkitMaskImage: FEATHER,
  WebkitMaskComposite: 'source-in',
  maskImage: FEATHER,
  maskComposite: 'intersect',
};

// Light taupe "Where Archen™ fits" close-out for the Buyer's Guide (LP-006).
export const ArchenFitSection = ({ s }) => (
  <section className="relative overflow-hidden bg-[#E3D9CB]">
    <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid lg:grid-cols-[1fr_1.25fr] gap-14 lg:gap-10 items-center">
        <div>
          <p className="text-[#8B6F47] text-[11px] tracking-[0.2em] uppercase">{s.eyebrow}</p>
          <div className="mt-4 mb-8 h-px w-16 bg-[#B7A88F]" />
          <h2 className="text-[#1F1812] leading-[1.08] tracking-[-0.01em]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.3rem, 4vw, 3.4rem)', maxWidth: s.h2Max }}>
            {s.h2}
          </h2>
          <p className="mt-6 text-[#1F1812] text-[16px] leading-[1.75] max-w-[46ch]">
            <span className="text-[#5C6B4E] font-semibold">{s.introHighlight}</span>
            {s.introRest}
          </p>

          <div className="mt-9 mb-9 h-px w-full bg-[#C4B7A2]" />

          <div className="space-y-7">
            {s.steps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="flex gap-4">
                  <div className="w-11 h-11 rounded-full border border-[#B7A88F] bg-white/60 flex items-center justify-center shrink-0">
                    <Icon size={18} strokeWidth={1.5} className="text-[#8B6F47]" />
                  </div>
                  <div>
                    <p className="text-[#1F1812] text-[15.5px] font-semibold">{step.title}</p>
                    <p className="mt-1 text-[#4A4038] text-[14px] leading-[1.6]">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative flex justify-center lg:justify-end">
          {s.image ? (
            <img
              data-testid="archen-fit-image"
              src={s.image.src}
              alt={s.image.alt}
              className="w-full max-w-[680px] h-auto rounded-2xl shadow-xl"
              style={FEATHER_STYLE}
            />
          ) : (
            <div
              data-testid="archen-fit-image-placeholder"
              className="w-full max-w-[440px] h-[360px] rounded-2xl border border-dashed border-[#B7A88F] flex items-center justify-center"
            >
              <p className="text-[#8B6F47] text-[13px] tracking-[0.1em] uppercase">Product screenshot coming</p>
            </div>
          )}
        </div>
      </div>
    </div>

    {s.quote && (
      <div className="border-t border-[#C4B7A2]">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-14 h-14 rounded-full border border-[#B7A88F] bg-white/60 flex items-center justify-center shrink-0">
            <Scale size={22} strokeWidth={1.4} className="text-[#8B6F47]" />
          </div>
          <p className="text-[#1F1812] leading-[1.35]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(1.5rem, 2.6vw, 2.1rem)' }}>
            {s.quote.lead}{' '}
            <span className="italic text-[#5C6B4E] underline underline-offset-4">{s.quote.emphasis}</span>
          </p>
        </div>
      </div>
    )}
  </section>
);

export default ArchenFitSection;
